import { Box, Typography } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";

type Props = {
	open: boolean;
	onClose: () => void;
	// Seed value for the census field whenever the dialog re-opens.
	initialCensus: number;
	// Where the seeded census came from (auto-computed / fallback) when it isn't a user
	// override — shown under the field so the number isn't an unexplained default.
	sourceHint?: string;
	// Disable saving when there's no draft to write into (parent owns the draft).
	saveDisabled?: boolean;
	// Parent persists the validated census into its draft.
	onSave: (census: number) => void;
};

// A whole-number average daily census of at least 1. Empty / non-numeric / < 1
// surfaces as an explicit error and blocks confirm rather than silently
// coercing to 0 (the old `Number.parseInt(...) || 0` behavior).
const parseCensus = (raw: string): number | null => {
	const trimmed = raw.trim();
	if (trimmed === "") return null;
	const n = Number(trimmed);
	if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) return null;
	return n;
};

export const CensusDialog: FC<Props> = ({ open, onClose, initialCensus, sourceHint, saveDisabled = false, onSave }) => {
	const { t } = useTranslation();
	const [value, setValue] = useState<string>(String(initialCensus));

	// Reset the working copy to the committed value each time the dialog opens so a
	// cancel → reopen never shows a stale draft.
	useEffect(() => {
		if (open) setValue(String(initialCensus));
	}, [open, initialCensus]);

	const parsed = parseCensus(value);
	const isValid = parsed !== null;

	const handleConfirm = () => {
		if (parsed === null) return;
		onSave(parsed);
		onClose();
	};

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			dataTestId="pbj-model-census-dialog"
			title={t("pbj_model.scoring.census_dialog.title")}
			confirm={t("save")}
			cancel={t("close")}
			confirmButtonVariant="primary"
			confirmDisabled={saveDisabled || !isValid}
			onConfirm={handleConfirm}
			body={
				<Box sx={{ pt: 1 }}>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
						{t("pbj_model.scoring.census_dialog.help")}
					</Typography>
					<SystemValidationTextField
						fullWidth
						label={t("pbj_model.config.average_census")}
						inputType="number"
						value={value}
						onChange={(e) => setValue(e.target.value)}
						defaultHelperText={sourceHint}
						validation={{
							validationCallback: (v: string) => parseCensus(v) !== null,
							helperText: { invalid: t("pbj_model.scoring.census_dialog.invalid") },
							performValidation: "onlyAfterInitialChange",
							showValidWhenValid: "never",
						}}
						inputProps={{ inputProps: { min: 1, step: 1, "data-testid": "pbj-model-census-input" } }}
					/>
				</Box>
			}
		/>
	);
};
